import React, { useEffect, useRef, useState } from 'react'
import SelectProjectForm from '../components/installation/SelectProjectForm'
import InstallDeviceToProject from '../components/installation/InstallDeviceToProject'
import { useTranslation } from 'react-i18next'
import PageHeader from '../components/PageHeader'
import { Container } from '@mui/material'
import { NavLink, useSearchParams } from 'react-router-dom'

enum InstallDeviceStep {
  SELECT_PROJECT = 0,
  INSTALL_IN_PROJECT = 1
}

export default function InstallDevice() {
  /**
   * The translate function.
   */
  const { t } = useTranslation()

  /**
   * Selected project
   */
  const [searchParams, setSearchParams] = useSearchParams()

  const [selectedProject, setSelectedProject] = useState<number | undefined>(() => {
    const p = searchParams.get('project')
    return p ? parseInt(p) : undefined
  })

  function getInitialActiveStep(): InstallDeviceStep {
    if (searchParams.has('step')) {
      return parseInt(searchParams.get('step') as string) as InstallDeviceStep
    }

    if (selectedProject !== undefined) {
      return InstallDeviceStep.INSTALL_IN_PROJECT
    }

    return InstallDeviceStep.SELECT_PROJECT
  }

  const [activeStep, setActiveStep] = useState<InstallDeviceStep>(() => getInitialActiveStep())
  const skipReplaceRef = useRef(false)
  const _prevSelectedProjectRef = useRef<number | undefined>(undefined)
  const _initialMountRef = useRef(true)

  // Keep internal state in sync with browser navigation (Back/Forward).
  // We initialize from search params at mount, but when the user navigates
  // via browser history react-router updates `searchParams`. Mirror that
  // into local `activeStep` and `selectedProject` so UI updates immediately.
  useEffect(() => {
    const s = searchParams.get('step')
    const stepFromUrl = s ? (parseInt(s) as InstallDeviceStep) : InstallDeviceStep.SELECT_PROJECT

    if (stepFromUrl !== activeStep) {
      setActiveStep(stepFromUrl)
    }

    const p = searchParams.get('project')
    const projectFromUrl = p ? parseInt(p) : undefined
    if (projectFromUrl !== selectedProject) {
      setSelectedProject(projectFromUrl)
    }
  }, [searchParams])

  // Keep search params in sync with selectedProject
  useEffect(() => {
    // Guard to avoid replacing a just-pushed history entry
    if (skipReplaceRef.current) {
      skipReplaceRef.current = false
      return
    }

    // During initial mount we only set the prev ref so we don't push/replace
    if (_initialMountRef.current) {
      _initialMountRef.current = false
      _prevSelectedProjectRef.current = selectedProject
      return
    }

    // If a project was just selected (prev undefined -> defined) push a new history entry
    // so Back navigates to the project selection view. This mirrors the previous
    // dedicated effect but keeps the logic in the single URL-sync effect.
    if (_prevSelectedProjectRef.current === undefined && selectedProject !== undefined) {
      const params = new URLSearchParams()
      params.set('step', String(InstallDeviceStep.INSTALL_IN_PROJECT))
      params.set('project', String(selectedProject))

      skipReplaceRef.current = true
      setSearchParams(params)

      // safety fallback to clear the guard in case other updates don't run
      setTimeout(() => {
        skipReplaceRef.current = false
      }, 0)

      _prevSelectedProjectRef.current = selectedProject
      return
    }

    // Otherwise, replace the current history entry so incidental state changes
    // (step/project) don't create navigation history entries.
    const params = new URLSearchParams()
    params.set('step', String(activeStep))

    if (selectedProject !== undefined) {
      params.set('project', String(selectedProject))
    }

    setSearchParams(params, { replace: true })

    _prevSelectedProjectRef.current = selectedProject
  }, [selectedProject, activeStep])

  return (
    <React.Fragment>
      <PageHeader title={t('installDevice')} />

      <Container maxWidth={false}>
        {selectedProject ? (
          <InstallDeviceToProject
            projectId={selectedProject}
            handleBackStep={() => {
              const params = new URLSearchParams()
              params.set('step', String(InstallDeviceStep.SELECT_PROJECT))
              setSearchParams(params)
              setSelectedProject(undefined)
            }}
          />
        ) : (
          <SelectProjectForm
            projectSelected={(project) => {
              setSelectedProject(project)
            }}
          />
        )}
      </Container>
    </React.Fragment>
  )
}
